/* Open state and the two key bindings the footer promises.
 *
 * **Its own file rather than a second export from `GuidePanel`**, because a module that exports
 * both a component and a hook defeats fast refresh — every keystroke in the guide's search box
 * would remount the panel and lose the field's focus.
 */

import { useEffect, useState } from 'react';

/** Whether the panel is open, plus the `?` and `Escape` bindings the reference declares.
 *
 * **A hook rather than state in `AppShell`**, so the keyboard contract lives with the panel it
 * drives: a shell that owned the listener would be a second place the binding could be changed, and
 * the footer promises the binding in words.
 */
export function useGuide(): {
  readonly open: boolean;
  readonly toggle: () => void;
  readonly close: () => void;
} {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      /* **Typing is never a shortcut.** `?` is an ordinary character in a search box, and without
         this a reader searching the catalogue for "why?" opens the guide mid-word. The reference
         guards the same three tags. `isContentEditable` is added because this app renders one. */
      const target = event.target;
      if (target instanceof HTMLElement) {
        const tag = target.tagName;
        if (
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT' ||
          target.isContentEditable
        ) {
          return;
        }
      }
      if (event.key === '?') setOpen((was) => !was);
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  return {
    open,
    toggle: () => {
      setOpen((was) => !was);
    },
    close: () => {
      setOpen(false);
    },
  };
}
